import { ref, computed } from 'vue'
import moment from 'moment'
import { useStore } from 'vuex'
import apiOpenUseCase from '@/usecase/apiOpenUseCase'

export interface IEmployeeDetail {
  id: string
  fullName: string
  phoneNumber?: string | null
  email?: string | null
  oldPersonnelNumber?: string | null
  personnelNumber?: string | null
  birthPlace?: string | null
  birthDate?: string | null
  gender?: string | null
  maritalStatus?: string | null
  address?: string | null
  postalCode?: string | null
  cityId?: string | null
  city?: string | null
  provinceId?: string | null
  province?: string | null
  highestEducationLevel?: string | null
  imageUrl?: string | null
  joinDate?: string | null
  authUserId?: string | null
  nfcCardIdentifier?: string | null
  latestEmployment?: {
    id?: string
    positionId?: string
    positionName?: string
    employmentTypeId?: string
    employmentTypeName?: string
    regionName?: string
    startDate?: string
    endDate?: string | null
    isActive?: boolean
  } | null
  documents?: Array<{
    id: string
    documentTypeId: string
    documentTypeName: string
    documentNumber: string
    documentFileUrl: string
    issuedDate: string
    expirationDate: string
  }>
  employments?: Array<{
    id: string
    positionId: string
    positionName: string
    employmentTypeId: string
    employmentTypeName: string
    regionName?: string
    startDate: string
    endDate?: string | null
    remarks?: string | null
    isActive: boolean
  }>
}

export interface ISsoUser {
  id: string
  username: string
  email: string
  firstName?: string
  lastName?: string
}

export function useCrewDetail() {
  const store = useStore()
  const employee = ref<IEmployeeDetail | null>(null)
  const ssoUser = ref<ISsoUser | null>(null)
  const isLoading = ref(false)
  const errorMessage = ref('')

  const formatDate = (val?: string | null): string => {
    if (!val) return '-'
    const m = moment(val)
    return m.isValid() ? m.format('DD/MM/YYYY') : '-'
  }

  const formatGender = (val?: string | null): string => {
    if (!val) return '-'
    const lower = val.toLowerCase()
    if (lower === 'male' || lower === 'laki-laki' || lower === 'pria') return 'Laki-Laki'
    if (lower === 'female' || lower === 'perempuan' || lower === 'wanita') return 'Perempuan'
    return val
  }

  const formatMaritalStatus = (val?: string | null): string => {
    if (!val) return '-'
    const lower = val.toLowerCase()
    if (lower === 'single' || lower === 'lajang' || lower === 'belum menikah') return 'Lajang'
    if (lower === 'married' || lower === 'menikah' || lower === 'kawin') return 'Menikah'
    if (lower === 'divorced' || lower === 'cerai') return 'Cerai'
    return val
  }

  const isActiveStatus = computed(() => {
    if (employee.value?.employments && employee.value.employments.length > 0) {
      return employee.value.employments.some((e) => e.isActive)
    }
    if (employee.value?.latestEmployment) {
      return employee.value.latestEmployment.isActive ?? false
    }
    return false
  })

  const activePositionName = computed(() => {
    const active = employee.value?.employments?.find((e) => e.isActive)
    if (active?.positionName) return active.positionName
    if (employee.value?.employments?.[0]?.positionName) return employee.value.employments[0].positionName
    return employee.value?.latestEmployment?.positionName || '-'
  })

  const ssoDisplay = computed(() => {
    if (!employee.value?.authUserId) {
      return 'Belum Tersedia'
    }
    if (ssoUser.value) {
      const parts = [
        ssoUser.value.username || '-',
        employee.value.phoneNumber || '-',
        ssoUser.value.email || '-'
      ]
      return parts.join(' | ')
    }
    return employee.value.phoneNumber ? `- | ${employee.value.phoneNumber} | -` : '-'
  })

  const fetchEmployeeDetail = async (id: string) => {
    isLoading.value = true
    errorMessage.value = ''
    try {
      const res = await apiOpenUseCase.get(`/talent/v1/employees/${id}`)
      const data = res?.result
      if (data && data.id) {
        employee.value = data
        store.dispatch('ui/setBreadcrumbs', [
          { title: 'Master Kru', routeName: 'master-crew' },
          { title: data.fullName || 'Detail Kru', routeName: 'master-crew-detail' }
        ])

        if (data.authUserId) {
          try {
            const ssoRes = await apiOpenUseCase.get(`talent/v1/externalAuth/users/${data.authUserId}`)
            ssoUser.value = ssoRes?.result ?? null
          } catch (e) {
            console.error('Failed to fetch SSO user in detail:', e)
            ssoUser.value = null
          }
        }
      } else {
        errorMessage.value = 'Data kru tidak ditemukan'
      }
    } catch (err) {
      console.error('Error fetching employee detail:', err)
      errorMessage.value = 'Gagal memuat data kru'
    } finally {
      isLoading.value = false
    }
  }

  return {
    employee,
    ssoUser,
    isLoading,
    errorMessage,
    formatDate,
    formatGender,
    formatMaritalStatus,
    isActiveStatus,
    activePositionName,
    ssoDisplay,
    fetchEmployeeDetail
  }
}
